// PriceSimulatorCreateEditModal – config-driven Create / Edit Project or Scenario modal for PPA.

import React, { FC, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';

import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';

import { colorConstants } from 'config/colorConstants';
import { ppaSimulationUrls } from 'config/urls';

import {
    SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY,
    usePriceSimulatorV3Context,
} from 'contexts/PriceSimulatorV3Context';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';

import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { sendNotification } from 'utils/helperMethods';
import {
    buildCreateProjectPayload,
    buildCreateScenarioPayload,
    getClearedValue,
    getPpaCreateProjectFormValidationIssues,
    getPpaCreateScenarioFormValidationIssues,
    getProjectFilterFields,
    getSelectedValues,
    isInputValueMissing,
    mapEditParamsToPpaFormValues,
    resolvePpaCreateProjectApiErrorMessage,
} from 'utils/ppaHelpers';
import PPAField, { getFieldKey, type PpaProjectFilterInclusion } from './PPAField';
import { getPpaCascadingFilterDisabledMessage } from './PPAField.helpers';

import {
    PpaProjectActionType,
    PpaScenarioActionType,
    type PPAEditProjectParams,
    type PPAFeatureConfig,
    type PPAInputConfig,
} from 'types/ppa';

const EMPTY_EDIT_PARAMS: PPAEditProjectParams = {};

const FALLBACK_SCENARIO_FORM_CONFIG: PPAFeatureConfig = {
    feature_id: 'create_scenario',
    feature_name: 'Create New Scenario',
    feature_type: 'form',
    options: {
        inputs: [
            {
                input_id: 'scenario_name',
                input_label: 'Scenario Name',
                input_type: 'string',
                placeholder: 'Enter scenario name',
            },
        ],
    },
};

type FormKind = 'project' | 'scenario';

type Props = {
    open?: boolean;
    onCancel?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish?: (id: number | string | null) => void;
    editParams?: PPAEditProjectParams | Record<string, any>;
    /** `scenario` shows only the scenario name field and calls create-scenario API. */
    formKind?: FormKind;
    /** Required when `formKind` is `scenario`. */
    projectId?: number;
    /** create | edit | duplicate — same API, different payload. */
    projectActionType?: PpaProjectActionType;
    /** create | rename | duplicate — same API, different payload. */
    scenarioActionType?: PpaScenarioActionType;
    /** Existing scenarios in the project (duplicate-name validation). */
    existingScenarios?: Array<{ scenario_id?: number | string; scenario_name: string }>;
};

function createFormConfigFromMetaData(
    scenarioManagerMeta: ReturnType<typeof usePriceSimulatorV3Context>['scenarioManagerMeta'],
    formKind: FormKind,
): PPAFeatureConfig | null {
    const featureId = formKind === 'scenario' ? 'create_scenario' : 'create_project';
    const raw = scenarioManagerMeta?.features?.find(
        (f) =>
            f.feature_id === featureId &&
            f.feature_type === 'form' &&
            f.options?.inputs &&
            f.options.inputs.length > 0,
    );
    if (!raw?.options?.inputs) {
        return formKind === 'scenario' ? FALLBACK_SCENARIO_FORM_CONFIG : null;
    }

    const inputs = raw.options.inputs as PPAInputConfig[];
    const filteredInputs =
        formKind === 'scenario'
            ? inputs.filter((input) => input.input_id === 'scenario_name')
            : inputs;

    if (formKind === 'scenario' && filteredInputs.length === 0) {
        return FALLBACK_SCENARIO_FORM_CONFIG;
    }

    return {
        feature_id: String(raw.feature_id ?? featureId),
        feature_name: raw.feature_name ?? (formKind === 'scenario' ? 'Create New Scenario' : 'Create Project'),
        feature_type: 'form',
        options: { inputs: filteredInputs },
    };
}

const PriceSimulatorCreateEditModal: FC<Props> = ({
    open = true,
    onCancel = () => {},
    onSubmitFinish = () => {},
    editParams = EMPTY_EDIT_PARAMS,
    formKind = 'project',
    projectId,
    projectActionType = PpaProjectActionType.Create,
    scenarioActionType = PpaScenarioActionType.Create,
    existingScenarios = [],
}) => {
    const queryClient = useQueryClient();
    const { scenarioManagerMeta } = usePriceSimulatorV3Context();
    const [formValues, setFormValues] = useState<Record<string, any>>({});
    const isScenarioForm = formKind === 'scenario';

    const featureConfig = useMemo(
        () => createFormConfigFromMetaData(scenarioManagerMeta, formKind),
        [scenarioManagerMeta, formKind],
    );

    const hydrateFormFromParams = isScenarioForm
        ? scenarioActionType !== PpaScenarioActionType.Create
        : projectActionType !== PpaProjectActionType.Create;
    const isProjectEdit = projectActionType === PpaProjectActionType.Edit;
    const editParamsSnapshot = hydrateFormFromParams ? JSON.stringify(editParams ?? {}) : '';

    const createProjectMutation = useCommonMutationHandler({
        url: ppaSimulationUrls.createProjectUrl,
        method: 'post',
    });
    const createScenarioMutation = useCommonMutationHandler({
        url: ppaSimulationUrls.createScenarioUrl,
        method: 'post',
    });
    const createEditProjectFn = isScenarioForm ? createScenarioMutation.mutate : createProjectMutation.mutate;
    const createProjectLoading = isScenarioForm
        ? createScenarioMutation.loading
        : createProjectMutation.loading;

    useEffect(() => {
        if (!open) {
            setFormValues({});
            return;
        }
        if (!featureConfig) {
            setFormValues({});
            return;
        }
        if (hydrateFormFromParams && editParamsSnapshot) {
            const params = JSON.parse(editParamsSnapshot) as Record<string, any>;
            if (Object.keys(params).length > 0) {
                setFormValues(mapEditParamsToPpaFormValues(featureConfig, params));
            }
            return;
        }
        if (!hydrateFormFromParams) {
            setFormValues({});
        }
    }, [
        open,
        hydrateFormFromParams,
        editParamsSnapshot,
        featureConfig,
        isScenarioForm,
        projectActionType,
        scenarioActionType,
    ]);

    const projectFilterFields = featureConfig && !isScenarioForm ? getProjectFilterFields(featureConfig) : [];
    const projectFilterFieldKeys = projectFilterFields.map((filter) => filter.fieldKey);

    const updateFieldValue = (fieldKey: string, newValue: any) => {
        const changedFilterIndex = projectFilterFieldKeys.indexOf(fieldKey);

        const clearedDependentFields =
            changedFilterIndex >= 0
                ? projectFilterFields
                      .slice(changedFilterIndex + 1)
                      .reduce((cleared, filter) => ({
                          ...cleared,
                          [filter.fieldKey]: getClearedValue(filter.inputConfig),
                      }), {})
                : {};

        setFormValues((current) => ({
            ...current,
            [fieldKey]: newValue,
            ...clearedDependentFields,
        }));
    };

    function hasMissingRequiredFields(): boolean {
        if (!featureConfig) return true;
        return featureConfig.options.inputs.some((inputConfig, index) => {
            if (isFieldDisabled(inputConfig, index)) {
                return false;
            }
            if (
                isScenarioForm &&
                scenarioActionType === PpaScenarioActionType.Create &&
                inputConfig.input_id === 'scenario_name'
            ) {
                return false;
            }
            return isInputValueMissing(inputConfig, formValues, index);
        });
    }

    function projectFilterPosition(inputIndex: number): number {
        return projectFilterFields.findIndex((filter) => filter.index === inputIndex);
    }

    function getPreviousProjectFilter(inputIndex: number) {
        const positionInFilterList = projectFilterPosition(inputIndex);
        return positionInFilterList > 0 ? projectFilterFields[positionInFilterList - 1] : undefined;
    }

    /** Cascading behavior: a project filter is enabled only after its parent has at least one selection. */
    function isFieldDisabled(inputConfig: PPAInputConfig, inputIndex: number): boolean {
        if (isScenarioForm || inputConfig.input_id !== 'project_filters') return false;
        const parentFilter = getPreviousProjectFilter(inputIndex);
        if (!parentFilter) return false;
        return getSelectedValues(formValues[parentFilter.fieldKey]).length === 0;
    }

    function getDisabledFieldMessage(inputConfig: PPAInputConfig, inputIndex: number): string | undefined {
        if (!isFieldDisabled(inputConfig, inputIndex)) return undefined;
        const parentFilter = getPreviousProjectFilter(inputIndex);
        return getPpaCascadingFilterDisabledMessage(parentFilter?.inputConfig.input_label);
    }

    const editProjectId =
        (editParams as PPAEditProjectParams)?.id ?? (editParams as PPAEditProjectParams)?.project_id;

    const editScenarioId =
        (editParams as Record<string, any>)?.scenario_id ?? (editParams as Record<string, any>)?.id;

    const formValidationIssues = useMemo(() => {
        if (!featureConfig) return [];
        if (isScenarioForm) {
            return getPpaCreateScenarioFormValidationIssues(featureConfig, formValues, {
                existingScenarios,
                editingScenarioId:
                    scenarioActionType === PpaScenarioActionType.Rename ? editScenarioId : null,
                isEdit: scenarioActionType === PpaScenarioActionType.Rename,
                isCreate: scenarioActionType === PpaScenarioActionType.Create,
            });
        }
        return getPpaCreateProjectFormValidationIssues(featureConfig, formValues);
    }, [
        featureConfig,
        formValues,
        existingScenarios,
        projectActionType,
        editProjectId,
        editScenarioId,
        isScenarioForm,
        scenarioActionType,
    ]);

    const issueMessageByFieldKey = useMemo(() => {
        const m: Record<string, string> = {};
        for (const issue of formValidationIssues) {
            if (!m[issue.fieldKey]) m[issue.fieldKey] = issue.message;
        }
        return m;
    }, [formValidationIssues]);

    const isSubmitButtonDisabled =
        hasMissingRequiredFields() ||
        formValidationIssues.length > 0 ||
        (isScenarioForm && projectId == null);

    const onSubmit = () => {
        if (createProjectLoading || isSubmitButtonDisabled || !featureConfig) return;
        if (isScenarioForm && projectId == null) return;

        const payload = isScenarioForm
            ? buildCreateScenarioPayload(featureConfig, formValues, {
                  projectId: projectId as number,
                  scenarioActionType,
                  scenarioId:
                      scenarioActionType === PpaScenarioActionType.Create ? null : editScenarioId,
              })
            : buildCreateProjectPayload(featureConfig, formValues, {
                  projectId: isProjectEdit ? editProjectId : undefined,
              });

        createEditProjectFn(
            { ...payload },
            {
                onSuccess: async (data: any) => {
                    // Scenario create/edit only updates scenarios for the current project;
                    // refetching the project list is for the index page after project changes.
                    if (!isScenarioForm) {
                        await queryClient.invalidateQueries({
                            queryKey: SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY,
                        });
                    }
                    const createdId = isScenarioForm
                        ? data?.data?.scenario_id ?? editScenarioId ?? null
                        : data?.data?.project_id ?? editProjectId ?? null;
                    const scenarioSuccessMessage =
                        scenarioActionType === PpaScenarioActionType.Rename
                            ? PricingSimulatorNotificationMessages.RENAME_SCENARIO_SUCCESS
                            : scenarioActionType === PpaScenarioActionType.Duplicate
                              ? PricingSimulatorNotificationMessages.DUPLICATE_SCENARIO_SUCCESS
                              : PricingSimulatorNotificationMessages.CREATE_SCENARIO_SUCCESS;
                    const projectSuccessMessage =
                        projectActionType === PpaProjectActionType.Edit
                            ? PricingSimulatorNotificationMessages.EDIT_PROJECT_SUCCESS
                            : PricingSimulatorNotificationMessages.CREATE_PROJECT_SUCCESS;
                    sendNotification(
                        'success',
                        isScenarioForm ? scenarioSuccessMessage : projectSuccessMessage,
                    );
                    onSubmitFinish(createdId);
                },
                onError: (error: any) => {
                    const errorMsg = resolvePpaCreateProjectApiErrorMessage(
                        error,
                        isScenarioForm
                            ? PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR
                            : PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                    );
                    sendNotification('error', errorMsg);
                },
            },
        );
    };

    const scenarioModalCopy: Record<PpaScenarioActionType, { title: string; button: string }> = {
        [PpaScenarioActionType.Create]: { title: 'Create New Scenario', button: 'Create Scenario' },
        [PpaScenarioActionType.Rename]: { title: 'Edit Scenario', button: 'Save' },
        [PpaScenarioActionType.Duplicate]: { title: 'Duplicate Scenario', button: 'Duplicate' },
    };

    const projectModalCopy: Record<PpaProjectActionType, { title: string; button: string }> = {
        [PpaProjectActionType.Create]: {
            title: featureConfig?.feature_name ?? 'Create Project',
            button: 'Create Project',
        },
        [PpaProjectActionType.Edit]: { title: 'Edit Project', button: 'Save Changes' },
        [PpaProjectActionType.Duplicate]: { title: 'Duplicate Project', button: 'Duplicate Project' },
    };

    const modalTitle = isScenarioForm
        ? scenarioModalCopy[scenarioActionType].title
        : projectModalCopy[projectActionType].title;
    const modalButtonTitle = isScenarioForm
        ? scenarioModalCopy[scenarioActionType].button
        : projectModalCopy[projectActionType].button;

    return (
        <CustomModal
            bodyStyle={{ background: colorConstants.greyAlt9 }}
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{
                style: { borderRadius: 6 },
                disabled: !featureConfig || isSubmitButtonDisabled,
            }}
            open={open}
            title={modalTitle}
            okText={
                createProjectLoading ? (
                    <Spin indicator={antIcon} style={{ color: 'white' }} />
                ) : (
                    modalButtonTitle
                )
            }
            cancelText="Cancel"
            width={650}
            centered
            maskClosable={false}
            destroyOnClose
            onCancel={(e) => {
                if (createProjectLoading) return;
                onCancel(e);
            }}
            onOk={onSubmit}
        >
                <div className="flex-col flex gap-6">
                    {featureConfig ? (
                        featureConfig.options.inputs.map((inputConfig, index) => {
                            const fieldKey = getFieldKey(inputConfig, index);

                            let priorProjectFilterInclusions: PpaProjectFilterInclusion[] | undefined;
                            if (inputConfig.input_id === 'project_filters') {
                                const position = projectFilterFields.findIndex((f) => f.fieldKey === fieldKey);
                                priorProjectFilterInclusions =
                                    position > 0
                                        ? projectFilterFields.slice(0, position).flatMap((f) =>
                                              getSelectedValues(formValues[f.fieldKey]).map((selectedValue) => {
                                                  const selectedValueText = String(selectedValue);
                                                  return {
                                                      dimension: f.inputConfig.dimension,
                                                      attribute_type: f.inputConfig.attribute_type,
                                                      attribute_ex_id: selectedValueText,
                                                      attribute_desc: selectedValueText
                                                          .toLowerCase()
                                                          .split('_')
                                                          .map(
                                                              (word) =>
                                                                  word.charAt(0).toUpperCase() + word.slice(1),
                                                          )
                                                          .join(' '),
                                                  };
                                              }),
                                          )
                                        : [];
                            }

                            return (
                                <PPAField
                                    key={fieldKey}
                                    fieldKey={fieldKey}
                                    inputConfig={inputConfig}
                                    value={formValues[fieldKey]}
                                    disabled={isFieldDisabled(inputConfig, index)}
                                    disabledMessage={getDisabledFieldMessage(inputConfig, index)}
                                    onChange={updateFieldValue}
                                    priorProjectFilterInclusions={priorProjectFilterInclusions}
                                    validationMessage={issueMessageByFieldKey[fieldKey]}
                                />
                            );
                        })
                    ) : (
                        <p className="m-0 text-[14px] leading-[22px] text-fontColor/70">
                            Form configuration is missing. Add a create_project form feature to scenario manager
                            metadata.
                        </p>
                    )}
                </div>
            </CustomModal>
    );
};

export default PriceSimulatorCreateEditModal;
